import React, {Suspense, useEffect, useRef, useState} from 'react';
import {TscStyles} from '@techsmith/tsc-cloud-style-guide';
import {IWatchAppModel} from '../model/appModel';
import {IMediaDetails} from '../model/mediaDetails';
import config from '../service/config';
import '../static/css/App.css';
import HeaderPlaceholder from './header/HeaderPlaceholder';
import VideoPlaceholder from './display/VideoPlaceholder';
import Analytics from '../service/analytics';
import Header from './header/Header';
import MediaContent from './content/MediaContent';
import {useTranslation} from 'react-i18next';
import {useTscTheme} from '../context/themeProvider';
import withMemoizedContexts from '../context/contextContainerHoC';
import {foldersStore, updateFoldersData} from '../context/foldersProvider';
import {IFolderTreeNode} from '../model/folderTreeNode';
import ScreenReaderHelper from './util/ScreenReaderHelper';
import {PageWrapper} from './util/StyledElements';
import {importMediaInterface} from '../hooks/dynamicImport';

export const AppBase: React.FC<IAppProps & IStateMappedProps> = ({watchAppModel, folderTree}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [media] = useState(watchAppModel.Media);

   const folderDataUpdated = useRef<boolean>(false);
   useEffect(() => {
      if (!folderDataUpdated.current) {
         void (folderTree || updateFoldersData(config.user.TechSmithId));
         folderDataUpdated.current = true;
      }
   }, [folderDataUpdated]);

   useEffect(() => {
      void Analytics.initialize();

      const mediaCustomDims: Partial<IMediaDetails> = {};
      Object.keys(media).filter(key => {
         const unnecessaryKeys = ['Author', 'Description', 'Groups', 'IsDecorated'];
         return !unnecessaryKeys.includes(key);
      }).forEach(key => {
         mediaCustomDims[key] = media[key];
      });
      media && Analytics.aiConfigureCustomDimensions(mediaCustomDims, 'media');

      window.addEventListener(TscStyles.events.modalOpenedOnPage, () => importMediaInterface().then(m => m.pause()));
   }, []);

   return (
      <PageWrapper theme={theme}>
         <ScreenReaderHelper
            href="#start-of-content"
            text={t('accessibility.skipLinkText')}
         />
         {!config.environmentData.IsLti && (
            <Suspense fallback={<HeaderPlaceholder />}>
               <Header
                  hideSearch={true}
                  hideEntitlementInfo={true}
                  hideThemePicker={false}
                  hideNavLinkTabs={false}
               />
            </Suspense>
         )}
         <div
            className="app-content-wrapper"
            id="start-of-content"
         >
            <Suspense fallback={<VideoPlaceholder />}>
               <MediaContent />
            </Suspense>
         </div>
      </PageWrapper>
   );
};

interface IAppProps {
   watchAppModel: IWatchAppModel;
}

interface IStateMappedProps {
   folderTree: IFolderTreeNode;
}

const mapStatesToProps = (
   {watchAppModel}: IAppProps,
   {folderTree}: Partial<IStateMappedProps>
): IStateMappedProps & IAppProps => ({watchAppModel, folderTree});

export default withMemoizedContexts(mapStatesToProps, foldersStore)(AppBase);
